import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './stores/auth'
import AppLayout from './components/layout/AppLayout'
import LoginPage from './pages/auth/LoginPage'
import DashboardPage from './pages/dashboard/DashboardPage'
import LeadsPage from './pages/leads/LeadsPage'
import LeadDetailPage from './pages/leads/LeadDetailPage'
import CompaniesPage from './pages/companies/CompaniesPage'
import TasksPage from './pages/tasks/TasksPage'
import EmailPage from './pages/email/EmailPage'
import QueuePage from './pages/queue/QueuePage'
import VoIPPage from './pages/voip/VoIPPage'
import UnknownCallersPage from './pages/voip/UnknownCallersPage'
import ReportsPage from './pages/reports/ReportsPage'
import UsersPage from './pages/users/UsersPage'
import SettingsPage from './pages/settings/SettingsPage'
import NotificationsPage from './pages/notifications/NotificationsPage'
import BlogPage from './pages/tools/BlogPage'
import SocialPage from './pages/tools/SocialPage'
import CampaignPage from './pages/tools/CampaignPage'
import StrategyPage from './pages/tools/StrategyPage'
import ImagePage from './pages/tools/ImagePage'
import ScraperPage from './pages/tools/ScraperPage'
import HistoryPage from './pages/tools/HistoryPage'
import MetaAdsPage from './pages/meta/MetaAdsPage'
import MetaAnalyticsPage from './pages/meta/MetaAnalyticsPage'
import DataTransfersPage from './pages/settings/DataTransfersPage'
import TeamsPage from './pages/team/TeamsPage'
import TeamWorkloadPage from './pages/team/TeamWorkloadPage'
import TaskMonitorPage from './pages/jobs/TaskMonitorPage'
import TeamAnalyticsPage from './pages/analytics/TeamAnalyticsPage'
import SocialAnalyticsPage from './pages/social/SocialAnalyticsPage'
import PresenceDashboardPage from './pages/presence/PresenceDashboardPage'
import IgDiscoveryPage from './pages/ig-discovery/IgDiscoveryPage'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function RoleRoute({ children, roles }: { children: React.ReactNode; roles: string[] }) {
  const user = useAuthStore((s) => s.user)
  if (!user || !roles.includes(user.role)) {
    // Marketing users land on tools, everyone else on leads
    const fallback = user?.role === 'marketing' ? '/tools/blog' : '/leads'
    return <Navigate to={fallback} replace />
  }
  return <>{children}</>
}

export default function App() {
  const { checkAuth, isLoading, isAuthenticated } = useAuthStore()

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <Routes>
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard — all roles get a home page */}
        <Route path="/" element={<RoleRoute roles={['admin', 'manager', 'sales_rep', 'marketing']}><DashboardPage /></RoleRoute>} />

        {/* Sales — admin/manager/sales_rep (not marketing) */}
        <Route path="/queue" element={<RoleRoute roles={['admin', 'manager', 'sales_rep']}><QueuePage /></RoleRoute>} />
        <Route path="/leads" element={<RoleRoute roles={['admin', 'manager', 'sales_rep']}><LeadsPage /></RoleRoute>} />
        <Route path="/leads/:id" element={<RoleRoute roles={['admin', 'manager', 'sales_rep']}><LeadDetailPage /></RoleRoute>} />
        <Route path="/companies" element={<RoleRoute roles={['admin', 'manager', 'sales_rep']}><CompaniesPage /></RoleRoute>} />
        <Route path="/tasks" element={<RoleRoute roles={['admin', 'manager', 'sales_rep', 'marketing']}><TasksPage /></RoleRoute>} />
        <Route path="/email" element={<RoleRoute roles={['admin', 'manager', 'sales_rep']}><EmailPage /></RoleRoute>} />
        <Route path="/voip" element={<RoleRoute roles={['admin', 'manager', 'sales_rep']}><VoIPPage /></RoleRoute>} />
        <Route path="/unknown-callers" element={<RoleRoute roles={['admin', 'manager']}><UnknownCallersPage /></RoleRoute>} />
        <Route path="/notifications" element={<NotificationsPage />} />

        {/* Tools — admin/manager/marketing */}
        <Route path="/tools/blog" element={<RoleRoute roles={['admin', 'manager', 'marketing']}><BlogPage /></RoleRoute>} />
        <Route path="/tools/social" element={<RoleRoute roles={['admin', 'manager', 'marketing']}><SocialPage /></RoleRoute>} />
        <Route path="/tools/campaign" element={<RoleRoute roles={['admin', 'manager', 'marketing']}><CampaignPage /></RoleRoute>} />
        <Route path="/tools/strategy" element={<RoleRoute roles={['admin', 'manager', 'marketing']}><StrategyPage /></RoleRoute>} />
        <Route path="/tools/image" element={<RoleRoute roles={['admin', 'manager', 'marketing']}><ImagePage /></RoleRoute>} />
        <Route path="/tools/scraper" element={<RoleRoute roles={['admin', 'manager', 'marketing']}><ScraperPage /></RoleRoute>} />

        {/* Insights — admin/manager (history/meta also marketing) */}
        <Route path="/reports" element={<RoleRoute roles={['admin', 'manager']}><ReportsPage /></RoleRoute>} />
        <Route path="/history" element={<RoleRoute roles={['admin', 'manager', 'marketing']}><HistoryPage /></RoleRoute>} />
        <Route path="/meta" element={<RoleRoute roles={['admin', 'manager', 'marketing']}><MetaAdsPage /></RoleRoute>} />
        <Route path="/meta/analytics" element={<RoleRoute roles={['admin', 'manager']}><MetaAnalyticsPage /></RoleRoute>} />
        <Route path="/social-analytics" element={<RoleRoute roles={['admin', 'manager', 'marketing']}><SocialAnalyticsPage /></RoleRoute>} />

        {/* Instagram Discovery — admin/manager/marketing/sales_rep */}
        <Route path="/ig-discovery" element={<RoleRoute roles={['admin', 'manager', 'marketing', 'sales_rep']}><IgDiscoveryPage /></RoleRoute>} />

        {/* Management — admin/manager */}
        <Route path="/team" element={<RoleRoute roles={['admin', 'manager']}><TeamsPage /></RoleRoute>} />
        <Route path="/team/workload" element={<RoleRoute roles={['admin', 'manager']}><TeamWorkloadPage /></RoleRoute>} />
        <Route path="/team/analytics" element={<RoleRoute roles={['admin', 'manager']}><TeamAnalyticsPage /></RoleRoute>} />
        <Route path="/presence" element={<RoleRoute roles={['admin', 'manager']}><PresenceDashboardPage /></RoleRoute>} />

        {/* Admin — admin only */}
        <Route path="/users" element={<RoleRoute roles={['admin']}><UsersPage /></RoleRoute>} />
        <Route path="/settings" element={<RoleRoute roles={['admin']}><SettingsPage /></RoleRoute>} />
        <Route path="/data-transfers" element={<RoleRoute roles={['admin', 'manager']}><DataTransfersPage /></RoleRoute>} />
        <Route path="/tasks-monitor" element={<RoleRoute roles={['admin']}><TaskMonitorPage /></RoleRoute>} />
      </Route>
    </Routes>
  )
}
